// src/lib/time/grid.ts
import type { TimeEntryType } from "@/lib/enums";
import { addDays, type IsoDate, monthStartOf, weekDates, weekdayShort } from "@/lib/time/dates";
import {
  entryLock,
  evaluateSave,
  formatHours,
  type LockReason,
  needsComment,
  normalizeComment,
  type PlanStatus,
  planStatus,
  type SaveContext,
} from "@/lib/time/rules";
import type { AssignableTask, PeriodRow, PlanRow, TimeEntryView } from "@/schemas/time";

/**
 * The weekly grid as data (spec "Grid", decisions 1-2 of the plan). Pure, so the component
 * only renders it and the tests pin the row and cell rules.
 */

export interface GridInput {
  weekStart: IsoDate;
  entries: TimeEntryView[];
  assignable: AssignableTask[];
  plans: PlanRow[];
  periods: PeriodRow[];
  thresholdPct: number;
}

type CellMode = "empty" | "single" | "multiple" | "locked";

export interface GridCell {
  key: string;
  date: IsoDate;
  /** What the cell shows: the sum of its entries, saved or not. */
  hours: number;
  /** What counts toward totals: excludes "Not saved" entries (billable 2h or more, no comment). */
  countedHours: number;
  /** True when an entry is billable 2h or more with no comment; it never counts toward totals. */
  notSaved: boolean;
  entries: TimeEntryView[];
  /** The one entry when mode is "single" (or a single locked entry), else null. */
  entry: TimeEntryView | null;
  mode: CellMode;
  lock: LockReason | null;
  editable: boolean;
}

export interface GridRow {
  key: string;
  projectTaskId: string;
  demandPersonId: string | null;
  taskName: string;
  projectName: string;
  /** From the assignable task; null when it is not assignable or the gateway gives none. */
  projectId: string | null;
  /** From the assignable task; null when the row is no longer assignable. */
  accountName: string | null;
  type: TimeEntryType;
  isFavorite: boolean;
  /** Null when the person can no longer log to this (task, assignment). */
  assignable: AssignableTask | null;
  plan: PlanStatus;
  cells: GridCell[];
  weekHours: number;
}

interface PtoRow {
  label: string;
  cells: { date: IsoDate; hours: number }[];
  weekHours: number;
}

export interface GridModel {
  days: IsoDate[];
  pto: PtoRow | null;
  rows: GridRow[];
  dayTotals: number[];
  weekTotal: number;
  ptoTotal: number;
}

export interface Suggestion {
  demandPersonId: string;
  demandName: string;
  label: string;
  tasks: AssignableTask[];
}

/**
 * A stored billable entry of 2h or more without a comment is "Not saved": shown, but never
 * counted. Stored internal entries keep counting: they were saved under the billable-only rule
 * (1,574 of them in 2026), and only a new save or an edit has to add the comment.
 */
const isNotSaved = (e: TimeEntryView) =>
  !e.isPto && e.taskIsBillable && needsComment(e.hours, e.comment);

const upper = (id: string | null) => (id ?? "").toUpperCase();
const sum = (values: number[]) => Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100;

export function rowKey(projectTaskId: string, demandPersonId: string | null): string {
  return `${upper(projectTaskId)}:${upper(demandPersonId)}`;
}

/** Two warehouse ids are the same id whatever their case (the gateway mixes them). */
export function sameId(a: string | null, b: string | null): boolean {
  return upper(a) === upper(b);
}

function monthPeriodFor(periods: PeriodRow[], date: IsoDate): PeriodRow | undefined {
  return periods.find((p) => p.periodType === "month" && p.start <= date && date <= p.end);
}

function weekPeriodFor(periods: PeriodRow[], date: IsoDate): PeriodRow | undefined {
  return periods.find((p) => p.periodType === "week" && p.start <= date && date <= p.end);
}

function planFor(
  plans: PlanRow[],
  demandPersonId: string | null,
  date: IsoDate,
): PlanRow | undefined {
  if (!demandPersonId) return undefined;
  return plans.find(
    (p) =>
      upper(p.demandPersonId) === upper(demandPersonId) &&
      p.monthStart <= date &&
      date <= p.monthEnd,
  );
}

/** A week belongs to the month it ends in (TE-35); its plan bar shows that month. */
function displayMonthOf(weekStart: IsoDate): IsoDate {
  return monthStartOf(addDays(weekStart, 6));
}

function periodLock(periods: PeriodRow[], date: IsoDate): LockReason | null {
  const month = monthPeriodFor(periods, date);
  const week = weekPeriodFor(periods, date);
  if (week?.signedOff || month?.signedOff) return "signed-off";
  if (!month || month.timeEntryClosed) return "month-closed";
  return null;
}

interface RowSeed {
  key: string;
  projectTaskId: string;
  demandPersonId: string | null;
  taskName: string;
  projectName: string;
  accountName: string | null;
  type: TimeEntryType;
  projectIsBillable: boolean;
  assignable: AssignableTask | null;
}

function buildCell(
  seed: RowSeed,
  date: IsoDate,
  entries: TimeEntryView[],
  periods: PeriodRow[],
): GridCell {
  const cellEntries = entries.filter(
    (e) =>
      !e.isPto &&
      e.projectTaskId !== null &&
      rowKey(e.projectTaskId, e.demandPersonId) === seed.key &&
      e.date === date,
  );
  const locks = cellEntries.map(entryLock).filter((l): l is LockReason => l !== null);
  let mode: CellMode =
    locks.length > 0
      ? "locked"
      : cellEntries.length > 1
        ? "multiple"
        : cellEntries.length === 1
          ? "single"
          : "empty";
  let lock: LockReason | null = locks[0] ?? null;
  const fromPeriod = periodLock(periods, date);
  if (lock === null && fromPeriod !== null) {
    lock = fromPeriod;
    if (mode === "empty" || mode === "single") mode = "locked";
  }
  return {
    key: `${seed.key}:${date}`,
    date,
    hours: sum(cellEntries.map((e) => e.hours)),
    countedHours: sum(cellEntries.filter((e) => !isNotSaved(e)).map((e) => e.hours)),
    notSaved: cellEntries.some(isNotSaved),
    entries: cellEntries,
    entry: cellEntries.length === 1 ? (cellEntries[0] ?? null) : null,
    mode,
    lock,
    editable: (mode === "empty" || mode === "single") && seed.assignable !== null,
  };
}

export function buildGrid(rawInput: GridInput): GridModel {
  const days = weekDates(rawInput.weekStart);
  // One definition of a counted entry for rows and totals: in the week, and either PTO or
  // attached to a task (a non-PTO entry with no task has no row, so it cannot count).
  const inWeek = new Set<string>(days);
  const input: GridInput = {
    ...rawInput,
    entries: rawInput.entries.filter(
      (e) => inWeek.has(e.date) && (e.isPto || e.projectTaskId !== null),
    ),
  };
  const assignableByKey = new Map<string, AssignableTask>();
  for (const a of input.assignable)
    assignableByKey.set(rowKey(a.projectTaskId, a.demandPersonId), a);

  const seeds = new Map<string, RowSeed>();
  for (const a of input.assignable) {
    if (!a.isFavorite) continue;
    const key = rowKey(a.projectTaskId, a.demandPersonId);
    seeds.set(key, {
      key,
      projectTaskId: a.projectTaskId,
      demandPersonId: a.demandPersonId,
      taskName: a.taskName,
      projectName: a.projectName,
      accountName: a.accountName,
      type: a.type,
      projectIsBillable: a.projectIsBillable,
      assignable: a,
    });
  }
  for (const e of input.entries) {
    if (e.isPto || e.projectTaskId === null) continue;
    const key = rowKey(e.projectTaskId, e.demandPersonId);
    if (seeds.has(key)) continue;
    const a = assignableByKey.get(key) ?? null;
    seeds.set(key, {
      key,
      projectTaskId: e.projectTaskId,
      demandPersonId: e.demandPersonId,
      taskName: a?.taskName ?? e.taskName,
      projectName: a?.projectName ?? e.projectName ?? "",
      accountName: a?.accountName ?? null,
      type: a?.type ?? e.type,
      projectIsBillable: a?.projectIsBillable ?? e.projectIsBillable,
      assignable: a,
    });
  }

  const displayMonth = displayMonthOf(input.weekStart);
  const rows: GridRow[] = [...seeds.values()]
    .map((seed) => {
      const cells = days.map((date) => buildCell(seed, date, input.entries, input.periods));
      const plan = planFor(input.plans, seed.demandPersonId, displayMonth);
      return {
        key: seed.key,
        projectTaskId: seed.projectTaskId,
        demandPersonId: seed.demandPersonId,
        taskName: seed.taskName,
        projectName: seed.projectName,
        projectId: seed.assignable?.projectId ?? null,
        accountName: seed.accountName,
        type: seed.type,
        isFavorite: seed.assignable?.isFavorite ?? false,
        assignable: seed.assignable,
        plan: planStatus({
          guidance: plan?.guidance ?? null,
          logged: plan?.logged ?? 0,
          thresholdPct: input.thresholdPct,
          projectIsBillable: seed.projectIsBillable,
          isInternal: seed.type === "internal",
        }),
        cells,
        weekHours: sum(cells.map((c) => c.countedHours)),
      };
    })
    .sort(
      (a, b) =>
        a.projectName.localeCompare(b.projectName) ||
        a.taskName.localeCompare(b.taskName) ||
        (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
    );

  const ptoEntries = input.entries.filter((e) => e.isPto);
  const ptoNames = [...new Set(ptoEntries.map((e) => e.taskName))];
  const pto: PtoRow | null =
    ptoEntries.length === 0
      ? null
      : {
          label: ptoNames.length === 1 ? (ptoNames[0] ?? "Time off") : "Time off",
          cells: days.map((date) => ({
            date,
            hours: sum(ptoEntries.filter((e) => e.date === date).map((e) => e.hours)),
          })),
          weekHours: sum(ptoEntries.map((e) => e.hours)),
        };

  const dayTotals = days.map((date) =>
    sum(input.entries.filter((e) => e.date === date && !isNotSaved(e)).map((e) => e.hours)),
  );
  return {
    days,
    pto,
    rows,
    dayTotals,
    weekTotal: sum(dayTotals),
    ptoTotal: pto?.weekHours ?? 0,
  };
}

/** One project's rows in the grid, with its day and week totals (counted hours). */
export interface GridGroup {
  key: string;
  projectName: string;
  accountName: string | null;
  rows: GridRow[];
  dayTotals: number[];
  weekHours: number;
  /**
   * The guidance every row here shares, shown on the heading instead of repeating on each task.
   * Null when the rows differ (two assignments on one project): each task keeps its own.
   */
  plan: PlanStatus | null;
}

/** Guidance belongs to the assignment, so one assignment's tasks all read the same. */
function sharedPlan(rows: GridRow[]): PlanStatus | null {
  const first = rows[0];
  if (!first?.demandPersonId) return null;
  const same = rows.every(
    (r) =>
      sameId(r.demandPersonId, first.demandPersonId) &&
      r.plan.state === first.plan.state &&
      r.plan.label === first.plan.label &&
      r.plan.text === first.plan.text,
  );
  return same ? first.plan : null;
}

/**
 * The grid's rows under their project, in the order the rows come (buildGrid sorts by project,
 * then task). A project is its id when the gateway gives one, else its name and client.
 */
export function groupGridRows(rows: GridRow[]): GridGroup[] {
  const groups = new Map<string, GridGroup>();
  for (const row of rows) {
    const key = row.projectId
      ? row.projectId.toUpperCase()
      : `${row.projectName}|${row.accountName ?? ""}`;
    const group = groups.get(key) ?? {
      key,
      projectName: row.projectName,
      accountName: row.accountName,
      rows: [],
      dayTotals: row.cells.map(() => 0),
      weekHours: 0,
      plan: null,
    };
    group.rows.push(row);
    group.dayTotals = group.dayTotals.map((t, i) => sum([t, row.cells[i]?.countedHours ?? 0]));
    group.weekHours = sum([group.weekHours, row.weekHours]);
    groups.set(key, group);
  }
  return [...groups.values()].map((group) => ({ ...group, plan: sharedPlan(group.rows) }));
}

/** Demands with guidance in the week's month and no row in the grid ("Suggested:"). */
export function buildSuggestions(input: GridInput, rows: GridRow[]): Suggestion[] {
  const displayMonth = displayMonthOf(input.weekStart);
  const visible = new Set(rows.map((r) => upper(r.demandPersonId)));
  return input.plans
    .filter(
      (p) =>
        p.monthStart === displayMonth &&
        (p.guidance ?? 0) > 0 &&
        !visible.has(upper(p.demandPersonId)),
    )
    .map((p) => {
      const demandName = p.demandName ?? "Guidance";
      return {
        demandPersonId: p.demandPersonId,
        demandName,
        label: `${demandName} · ${formatHours(p.logged)} of ${formatHours(p.guidance ?? 0)} hrs`,
        tasks: input.assignable.filter((a) => upper(a.demandPersonId) === upper(p.demandPersonId)),
      };
    })
    .filter((s) => s.tasks.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The browser's version of the server's entry-check read, for live messages. The browser has
 * no task totals, so it never warns "Over the task limit": that warning is never a block and
 * arrives in the save answer (the entry check sums the task's hours).
 */
export function clientSaveContext(args: {
  assignable: AssignableTask | null;
  date: IsoDate;
  plans: PlanRow[];
  periods: PeriodRow[];
  thresholdPct: number;
  demandPersonId: string | null;
  excludeHours: number;
}): SaveContext {
  const a = args.assignable;
  const month = monthPeriodFor(args.periods, args.date);
  const week = weekPeriodFor(args.periods, args.date);
  const plan = planFor(args.plans, args.demandPersonId, args.date);
  return {
    isAssignable: a !== null,
    projectIsBillable: a?.projectIsBillable ?? false,
    isInternal: a?.type === "internal",
    guidance: plan?.guidance ?? null,
    monthLogged: Math.max(0, (plan?.logged ?? 0) - args.excludeHours),
    thresholdPct: args.thresholdPct,
    monthPeriodFound: month !== undefined,
    monthClosed: month?.timeEntryClosed ?? false,
    periodSignedOff: (week?.signedOff ?? false) || (month?.signedOff ?? false),
    hoursLimit: null,
    taskLogged: 0,
  };
}

export type CellAction =
  | { kind: "none" }
  | { kind: "invalid"; message: string }
  | { kind: "needs-comment"; hours: number }
  | { kind: "blocked"; hours: number; message: string }
  | { kind: "create"; hours: number; comment: string | null }
  | { kind: "update"; entry: TimeEntryView; hours: number; comment: string | null }
  | { kind: "delete"; entry: TimeEntryView };

const HOURS_TEXT = "Hours must be 0.25 to 24";
const STEP_TEXT = "Use quarter hours";
/** Plain decimals only: "1", "1.5", ".25", "-1". Not "1e1", "0x10" or "1,5". */
const DECIMAL = /^-?(\d+\.?\d*|\.\d+)$/;

/** True for typed hours the grid and Log time accept as a number (trimmed). */
export function isDecimalText(raw: string): boolean {
  return DECIMAL.test(raw.trim());
}

/**
 * What a grid cell edit becomes. Empty or 0 deletes the cell's one entry; a new value
 * updates it (keeping its comment unless one is given) or creates one; a value the rules
 * block stays in the cell as "Not saved" and is never sent.
 */
export function cellAction(args: {
  cell: GridCell;
  raw: string;
  comment: string | null;
  ctx: SaveContext;
  today: IsoDate;
}): CellAction {
  const trimmed = args.raw.trim();
  if (trimmed !== "" && !isDecimalText(trimmed)) return { kind: "invalid", message: STEP_TEXT };
  const hours = trimmed === "" ? 0 : Number(trimmed);
  if (hours < 0 || hours > 24) return { kind: "invalid", message: HOURS_TEXT };
  const current = args.cell.entry;
  if (hours === 0) return current ? { kind: "delete", entry: current } : { kind: "none" };
  const comment = normalizeComment(args.comment ?? current?.comment ?? null);
  if (current && hours === current.hours && comment === normalizeComment(current.comment)) {
    return { kind: "none" };
  }
  // An edit carries the stored hours so lowering an over-plan entry passes, as on the server.
  const ctx = current ? { ...args.ctx, previousHours: current.hours } : args.ctx;
  const verdict = evaluateSave(ctx, { date: args.cell.date, hours, comment }, args.today);
  // A real block (over plan, no plan, closed...) wins over "needs a comment": a comment would
  // not make it save.
  const hard = verdict.blocks.find((b) => b.code !== "comment-required");
  if (hard) return { kind: "blocked", hours, message: hard.text };
  if (verdict.blocks.length > 0) return { kind: "needs-comment", hours };
  return current
    ? { kind: "update", entry: current, hours, comment }
    : { kind: "create", hours, comment };
}

export interface RowStatus {
  text: string;
  tone: "danger" | "warning";
}

/**
 * The row's status chip. A held value's block wins; otherwise only what logged hours say
 * (over plan, over the threshold). No plan alone shows no chip (UI standard 7).
 */
export function rowStatus(plan: PlanStatus, blockedText: string | undefined): RowStatus | null {
  if (blockedText) return { text: blockedText, tone: "danger" };
  if (!plan.text) return null;
  return { text: plan.text, tone: plan.state === "blocked" ? "danger" : "warning" };
}

/** The phone layout's first day: today when it is in the week, else Monday. */
export function defaultDayIndex(days: IsoDate[], today: IsoDate): number {
  const i = days.indexOf(today);
  return i >= 0 ? i : 1;
}

/** TE-25: the grid asks before a cell cleared to empty or 0 deletes its entry. */
export function deleteCellTitle(hours: number, date: IsoDate): string {
  const text = formatHours(hours);
  return `Delete ${text} ${text === "1" ? "hr" : "hrs"} on ${weekdayShort(date)}?`;
}
